<template>
    <DForm @submit.prevent="submitForm">
        <DFormRow
            :label="__('settings.tax-rules-credit-notes.form.country_name')"
            :is-required="true"
            :hint="__('settings.tax-rules-credit-notes.form.country_name_hint')"
        >
            <DSelect
                id="country_code"
                v-model="form.country_code"
                :disabled="isDefaultTaxRule"
                :options="countries"
                add-input-classes="bg-light"
                :placeholder="'Select country'"
                :is-valid="isFormFieldValid('country_code')"
                :invalid-feedback="errors.country_code"
            />
        </DFormRow>
        <DynamicTable
            v-model="form.tax_rules_credit_note_rates"
            :fields="getFields"
            :transformers="transformers"
            :new-row-data="getNewRowData()"
            :modal-title-add="__('settings.tax-rules-credit-notes.modal.title_add')"
            :modal-title-edit="__('settings.tax-rules-credit-notes.modal.title_edit')"
            :errors="errorsData"
            error-prefix="tax_rules_credit_note_rates"
            @update:modelValue="setRates"
        >
            <template #modalForm="{ item }">
                <DFormRow
                    :label="__('settings.tax-rules-credit-notes.form.valid_from')"
                    :is-required="true"
                    :error="errorsData[`tax_rules_credit_note_rates.${item.index}.valid_from`]"
                    :hint="__('settings.tax-rules-credit-notes.form.valid_from_hint')"
                >
                    <DDateFormatInput
                        id="valid_from"
                        v-model="item.valid_from"
                        format="YYYY-MM-DD"
                        type="date"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.tax-rules-credit-notes.form.tax_rate')"
                    :is-required="true"
                    :error="errorsData[`tax_rules_credit_note_rates.${item.index}.tax_rate`]"
                    :hint="__('settings.tax-rules-credit-notes.form.tax_rate_hint')"
                >
                    <DInput
                        id="tax_rate"
                        v-model="item.tax_rate"
                        add-input-classes="bg-light"
                        maxlength="5"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.tax-rules-credit-notes.form.tax_key')"
                    :is-required="true"
                    :error="errorsData[`tax_rules_credit_note_rates.${item.index}.tax_key`]"
                    :hint="__('settings.tax-rules-credit-notes.form.tax_key_hint')"
                >
                    <DInput
                        id="tax_key"
                        v-model="item.tax_key"
                        add-input-classes="bg-light"
                        type="string"
                    />
                </DFormRow>
                <DFormRow
                    v-if="isFinanceSystemNetsuite === false"
                    :label="__('settings.tax-rules-credit-notes.form.expense_account')"
                    :is-required="true"
                    :error="errorsData[`tax_rules_credit_note_rates.${item.index}.expense_account`]"
                    :hint="__('settings.tax-rules-credit-notes.form.expense_account_hint')"
                >
                    <DInput
                        id="expense_account"
                        v-model="item.expense_account"
                        add-input-classes="bg-light"
                        min="0"
                        max="2147483647"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.tax-rules-credit-notes.form.no_tax')"
                    :is-required="true"
                    :error="errorsData[`tax_rules_credit_note_rates.${item.index}.no_tax`]"
                    class="mt-2"
                    :hint="__('settings.tax-rules-credit-notes.form.no_tax_hint')"
                >
                    <DFormCheck
                        id="no_tax"
                        v-model="item.no_tax"
                        :label=" __('settings.tax-rules-credit-notes.form.no_tax')"
                        inline
                    />
                </DFormRow>
            </template>
        </DynamicTable>

        <DFormRow
            :label="__('settings.tax-rules-credit-notes.form.effectivity')"
            :is-required="true"
            :hint="__('settings.tax-rules-credit-notes.form.effectivity_hint')"
        >
            <DSelect
                id="effectivity"
                v-model="form.effectivity"
                :options="effectiveness"
                :is-valid="isFormFieldValid('effectivity')"
                :invalid-feedback="errors.effectivity"
                add-input-classes="bg-light"
            />
        </DFormRow>

        <DFormRow
            :label="__('settings.tax-rules-credit-notes.form.customer_type')"
            :is-required="true"
            :hint="__('settings.tax-rules-credit-notes.form.customer_type_hint')"
        >
            <DSelect
                id="customer_type"
                v-model="form.customer_type"
                :options="customerTypeOptions"
                add-input-classes="bg-light"
                :placeholder="__('settings.tax-rules-credit-notes.form.select_customer_type')"
                :is-valid="isFormFieldValid('customer_type')"
                :invalid-feedback="errors.customer_type"
            />
        </DFormRow>
        <DFormRow
            :label="__('settings.tax-rules-credit-notes.form.credit_note_type_name')"
            :is-required="true"
            :hint="__('settings.tax-rules-credit-notes.form.credit_note_type_name_hint')"
        >
            <DSelect
                id="credit_note_type"
                v-model="form.credit_note_type_id"
                :options="creditNoteTypes"
                add-input-classes="bg-light"
                :placeholder="__('settings.tax-rules-credit-notes.form.select_credit_note_type')"
                :is-valid="isFormFieldValid('credit_note_type_id')"
                :invalid-feedback="errors.credit_note_type_id"
            />
        </DFormRow>
        <DFormRow
            :label="__('settings.tax-rules-credit-notes.form.text')"
            :hint="__('settings.tax-rules-credit-notes.form.text_hint')"
        >
            <DInput
                id="text"
                v-model="form.text"
                add-input-classes="bg-light"
                :is-valid="isFormFieldValid('text')"
                :invalid-feedback="errors.text"
            />
            <TranslationLink
                :tenant-languages="tenantLanguages"
                :route="$route('settings.tax-rules-credit-notes.translations', {taxRulesCreditNotes: taxRuleCreditNote?.id ?? 0})"
                :default-tenant-language="defaultTenantLanguage"
            />
        </DFormRow>
        <DFormControls
            :cancel-route="$route('settings.tax-rules-credit-notes.index')"
            :dirty="form.isDirty"
            @submit="submitForm"
        />
    </DForm>
</template>

<script>
import TranslationLink from '../TranslationLink.vue'
import DynamicTable from '~@/Components/Common/DynamicComponents/DynamicTable.vue'

export default {
    name: 'TaxRuleForm',
    components: { DynamicTable, TranslationLink },
    props: {
        countries: {
            type: Array,
            default: () => { return [] }
        },
        errors: {
            type: Object,
            default: () => { return {} }
        },
        taxRuleCreditNote: {
            type: Object,
            default: () => { return {} }
        },
        customerTypes: {
            type: Array,
            default: () => { return [] }
        },
        creditNoteTypes: {
            type: Array,
            default: () => { return [] }
        },
        defaultTenantLanguage: {
            type: String,
            default: ''
        },
        tenantLanguages: {
            type: Array,
            default: () => { return [] }
        },
        effectiveness: {
            type: Array,
            default: () => { return [] }
        },
        isFinanceSystemNetsuite: {
            type: Boolean,
            default: () => { return false }
        }
    },
    emits: ['form-submitted'],
    data () {
        return {
            form: this.$inertia.form({
                country_code: this.taxRuleCreditNote?.country_code ?? this.$page.props.defaultCountry,
                customer_type: this.taxRuleCreditNote?.customer_type ?? null,
                credit_note_type_id: this.taxRuleCreditNote?.credit_note_type_id ?? null,
                text: this.taxRuleCreditNote?.text ?? null,
                effectivity: this.taxRuleCreditNote?.effectivity ?? null,
                tax_rules_credit_note_rates: []
            }),
            transformers: {
                valid_from: (item) => this.setDateFormat(item, 'DD.MM.YYYY'),
                valid_until: (item) => this.setDateFormat(item, 'DD.MM.YYYY'),
                tax_rate: (item) => item ? this.taxRateFormat(item) : '',
                no_tax: (item) => item ? this.__('settings.tax-rules-credit-notes.index.table.values.no_tax.yes') : this.__('settings.tax-rules-credit-notes.index.table.values.no_tax.no')
            },
            errorsData: this.errors
        }
    },
    computed: {
        isDefaultTaxRule () {
            return this.taxRuleCreditNote && this.taxRuleCreditNote.id && !this.taxRuleCreditNote.country_code
        },
        customerTypeOptions () {
            const types = this.customerTypes

            for (const index in types) {
                if (types[index].label.length !== 0) {
                    types[index].label = this.__(`customers.customers.customer_types.${types[index].value}`)
                }
            }

            return types
        },
        getFields () {
            if (this.isFinanceSystemNetsuite === true) {
                return [
                    { key: 'valid_from', label: this.__('settings.tax-rules-credit-notes.form.valid_from') },
                    { key: 'valid_until', label: this.__('settings.tax-rules-credit-notes.form.valid_until') },
                    { key: 'tax_rate', label: this.__('settings.tax-rules-credit-notes.form.tax_rate') },
                    { key: 'tax_key', label: this.__('settings.tax-rules-credit-notes.form.tax_key') },
                    { key: 'no_tax', label: this.__('settings.tax-rules-credit-notes.form.no_tax') },
                    { key: 'actions', label: '' }
                ]
            }

            return [
                { key: 'valid_from', label: this.__('settings.tax-rules-credit-notes.form.valid_from') },
                { key: 'valid_until', label: this.__('settings.tax-rules-credit-notes.form.valid_until') },
                { key: 'tax_rate', label: this.__('settings.tax-rules-credit-notes.form.tax_rate') },
                { key: 'tax_key', label: this.__('settings.tax-rules-credit-notes.form.tax_key') },
                { key: 'expense_account', label: this.__('settings.tax-rules-credit-notes.form.expense_account') },
                { key: 'no_tax', label: this.__('settings.tax-rules-credit-notes.form.no_tax') },
                { key: 'actions', label: '' }
            ]
        }
    },
    watch: {
        errors (newErrors) {
            this.errorsData = newErrors
        }
    },
    created () {
        this.setRates(this.taxRuleCreditNote?.tax_rules_credit_note_rates ?? [])
    },
    methods: {
        submitForm () {
            this.$emit('form-submitted', this.form)
        },
        setRates (data) {
            this.errorsData = {}
            // Sort by start date
            data = data.sort(function (a, b) {
                // Turn your strings into dates, and then subtract them
                // to get a value that is either negative, positive, or zero.
                return new Date(b.valid_from) - new Date(a.valid_from)
            })

            const rates = []

            let indexTrack = -1

            data.forEach((rate, index) => {
                const startDateOfNextRecord = data[index - 1]?.valid_from ?? null
                const date = startDateOfNextRecord ? new Date(startDateOfNextRecord) : null

                rates.push({
                    index: ++indexTrack,
                    id: rate.id,
                    valid_from: rate.valid_from,
                    valid_until: date !== null ? new Date(date.setDate(date.getDate() - 1)).toISOString() : null,
                    tax_rate: rate.tax_rate ?? null,
                    tax_key: rate.tax_key ?? null,
                    no_tax: rate.no_tax ?? false,
                    expense_account: rate.expense_account ?? null
                })
            })

            this.form.tax_rules_credit_note_rates = rates
        },
        getNewRowData () {
            return {
                index: 0,
                id: null,
                valid_from: null,
                valid_until: null,
                tax_rate: null,
                tax_key: null,
                no_tax: false,
                expense_account: this.isFinanceSystemNetsuite === false ? null : 0
            }
        },
        taxRateFormat (value) {
            return parseFloat(value).toFixed(2)
        }
    }
}
</script>
